<template>
  <div class="pp-stack" data-test="pp-tab-appointments">
    <PanelCard
      title="Upcoming"
      icon="fa-calendar"
      :count="upcoming.length || '—'"
      :table="upcoming.length > 0"
      test-id="pp-upcoming"
    >
      <table v-if="upcoming.length" class="pp-tbl">
        <thead><tr><th>When</th><th>Procedures</th><th>Provider</th><th>Note</th></tr></thead>
        <tbody>
          <tr v-for="(a, i) in upcoming" :key="i">
            <td class="pp-tbl__num">{{ dateTime(a.AptDateTime) }}</td>
            <td>{{ a.ProcDescript || '—' }}</td>
            <td>{{ a.Provider || '—' }}</td>
            <td class="pp-tbl__note">{{ a.AppointmentNotes || '' }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="pp-empty">Nothing scheduled from today on.</p>
    </PanelCard>

    <PanelCard
      title="Planned appointments"
      icon="fa-list-alt"
      :count="planned.length || '—'"
      :table="planned.length > 0"
      test-id="pp-planned"
    >
      <table v-if="planned.length" class="pp-tbl">
        <thead><tr><th>Procedures</th><th>Provider</th><th>Note</th></tr></thead>
        <tbody>
          <tr v-for="a in planned" :key="a.AptNum">
            <td>{{ a.ProcDescript || '—' }}</td>
            <td>{{ a.Provider || '—' }}</td>
            <td class="pp-tbl__note">{{ a.Note || '' }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="pp-empty">No planned appointments.</p>
    </PanelCard>

    <PanelCard
      v-if="asap.length"
      title="ASAP list"
      icon="fa-bolt"
      :count="asap.length"
      table
      test-id="pp-asap"
    >
      <table class="pp-tbl">
        <thead><tr><th>Appointment</th><th>Status</th><th>Procedures</th><th>Available</th><th>Notes</th></tr></thead>
        <tbody>
          <tr v-for="a in asap" :key="a.AptNum">
            <td class="pp-tbl__num">{{ date(a.AppointmentDate) }}</td>
            <td><span class="pp-pill pp-pill--slate">{{ a.AptStatus || '—' }}</span></td>
            <td>{{ a.ProcDescript || '—' }}</td>
            <td>{{ a.AvailableDays || '—' }}</td>
            <td class="pp-tbl__note">{{ a.AsapNotes || a.Note || '' }}</td>
          </tr>
        </tbody>
      </table>
    </PanelCard>

    <PanelCard
      title="Broken appointment history"
      icon="fa-times-circle-o"
      :count="broken.length || '—'"
      :table="broken.length > 0"
      test-id="pp-broken"
    >
      <table v-if="broken.length" class="pp-tbl">
        <thead><tr><th>Date</th><th>Recorded as</th><th>Provider</th></tr></thead>
        <tbody>
          <tr v-for="(b, i) in broken" :key="i">
            <td class="pp-tbl__num">{{ date(b.Date) }}</td>
            <td>
              <span class="pp-pill pp-pill--dot" :class="`pp-pill--${brokenKind(b)}`" data-test="pp-broken-kind">{{ b.AdjustProcCode || '—' }}</span>
            </td>
            <td>{{ b.Provider || '—' }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="pp-empty">No broken appointments in the last five years.</p>
    </PanelCard>
  </div>
</template>

<script>
/**
 * Appointments — from Patient Profile's own queries: scheduled (today on), planned, ASAP and the
 * broken-appointment history (its 5-year window). The design's "Visit history" with production is
 * not here: nothing in Teamio loads it yet (Phase 7 decision, 2026-09-25).
 *
 * ⛔ No prop named `patNum`, `siteID` or `loading` — mixin.js computeds silently replace them.
 */
import PanelCard from '../PanelCard.vue';
import { formatOdDate, formatOdDateTime } from '@/utils/patientPanel/format';

export default {
  name: 'AppointmentsTab',

  components: { PanelCard },

  props: {
    payload: { type: Object, default: () => ({}) },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
  },

  computed: {
    /**
     * Soonest first. The query returns newest first (Patient Profile's order), but "upcoming"
     * is read top-down as "what is next". OD datetimes are "YYYY-MM-DD HH:MM:SS", so a string
     * sort is a time sort — no Date, no timezone.
     */
    upcoming() {
      return ((this.payload && this.payload.scheduled) || [])
        .slice()
        .sort((a, b) => String(a.AptDateTime).localeCompare(String(b.AptDateTime)));
    },
    planned() {
      return (this.payload && this.payload.planned) || [];
    },
    asap() {
      return (this.payload && this.payload.asap) || [];
    },
    broken() {
      return (this.payload && this.payload.broken) || [];
    },
  },

  methods: {
    /**
     * Design 1a: a missed appointment (D9986, or an adjustment that says "missed"/"broken") is red,
     * a cancelled one (D9987 / "cancel") amber. Anything else keeps the missed colour — every row
     * here is a broken appointment of some kind.
     */
    brokenKind(b) {
      const t = String((b && b.AdjustProcCode) || '').toLowerCase();
      if (/d9987|cancel/.test(t)) return 'cancelled';
      return 'missed';
    },
    date(v) {
      return formatOdDate(v, this.dateFormat) || '—';
    },
    dateTime(v) {
      return formatOdDateTime(v, this.dateFormat) || '—';
    },
  },
};
</script>
